import PoiStatusIcon from '@components/Shared/PoiStatusIcon';
import type { Inspection } from '@lib/spproject/types';
import { Badge, Box, Divider, Stack, Typography } from '@mui/material';
import { FileText, ImageIcon } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import {
  MoreResourcesAvatar,
  PoiContainer,
  PoiContent,
  PoiDescription,
  PoiInfo,
  PoiTitle,
  ResourceAvatar,
  StatusAvatar,
} from './Inspections.styled';
import useNoticeColors from '@store/hooks/useNoticeColors';

interface InspectionPointItemProps {
  poi: Inspection.PointOfInterest;
  inspectionId: string;
  onPoiClick: (inspectionId: string, poi: Inspection.PointOfInterest) => void;
  isFirst: boolean;
}

const InspectionPointItem = memo<InspectionPointItemProps>(({ poi, inspectionId, onPoiClick, isFirst }) => {
  const noticeColors = useNoticeColors();

  const currentIndex = useMemo(() => {
    const idx = poi.stepIndex ?? 0;
    const max = Math.max(0, poi.steps.length - 1);
    return Math.min(Math.max(0, idx), max);
  }, [poi.stepIndex, poi.steps.length]);

  const currentStep = poi.steps[currentIndex];
  const noticeType = currentStep?.noticeType ?? 'NotChecked';

  const resources = useMemo(() => currentStep?.resources ?? [], [currentStep?.resources]);
  const visibleResources = useMemo(() => {
    return resources.length > 3 ? resources.slice(0, 3) : resources;
  }, [resources]);

  const extraCount = resources.length > 3 ? (resources.length - 3) : 0;
  const handleClick = useCallback(() => {
    onPoiClick(inspectionId, poi);
  }, [inspectionId, poi, onPoiClick]);

  return (
    <PoiContainer onClick={handleClick} style={{ cursor: 'pointer' }}>
      {!isFirst && <Divider />}
      <PoiContent>
        <Badge
          sx={{ marginRight: '16px' }}
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Box sx={{ backgroundColor: '#ccc', borderRadius: '100%', padding: '4px' }}>
              <PoiStatusIcon status={noticeType} />
            </Box>
          }>
          <StatusAvatar statusColor={noticeColors[noticeType]}>
            {poi.order}
          </StatusAvatar>
        </Badge>
        <PoiInfo>
          <PoiTitle>
            <Typography variant="body2" fontWeight="medium">
              {poi.name}
            </Typography>
          </PoiTitle>
          {poi.description && <PoiDescription variant="body2">{poi.description}</PoiDescription>}
        </PoiInfo>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: 'flex', mr: 1 }}>
            {visibleResources.map((resource, i) => (
              <ResourceAvatar key={i} index={i}>
                {resource.type === 'Pdf'
                  ? <FileText size={20} color="#f44336" />
                  : <ImageIcon size={20} color="#2196f3" />}
              </ResourceAvatar>
            ))}
            {extraCount > 0 && (
              <MoreResourcesAvatar>+{extraCount}</MoreResourcesAvatar>
            )}
          </Box>
        </Stack>
      </PoiContent>
    </PoiContainer>
  );
});

export default InspectionPointItem;
